/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useLayoutEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useDropzone } from "react-dropzone";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import {
	Alert,
	Box,
	Button,
	Chip,
	Dialog,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Tab,
	Tabs,
	Tooltip,
	Typography,
} from "@mui/material";
import { SiMicrosoftexcel } from "react-icons/si";
import { BiBlock } from "react-icons/bi";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import "react-datepicker/dist/react-datepicker.css";

import { useCore } from "../../../shared/hooks/use-core";
import * as api from "../../../shared/lib/api";
import { Paginator } from "../../../shared/components/paginator";
import { useAuth } from "../../../features/auth/hooks/use-auth";
import { palette, StatusColor } from "../../../shared/styles/theme";
import DoorRulesTab from "./DoorRulesTab";
import NotAuthorized from "./NotAuthorized";

type PrecificadorTab = "emplacamentos" | "regras-portas";

const DOOR_RULES_TAB: PrecificadorTab = "regras-portas";

type UploadStatus = "PENDING" | "PROCESSING" | "MATCHING" | "DONE" | "FAILED";

type UploadRow = {
	id: string;
	original_name: string;
	size_bytes: number;
	status: UploadStatus;
	reference_month?: string | null;
	created_at: string;
	updated_at: string;
	error_count: number;
	row_count: number | null;
	total_rows: number | null;
	progress_percent: number | null;
};

type ListResponse = {
	count?: number;
	results?: UploadRow[];
};

registerLocale("ptBR", ptBR as any);

const BASE_PATH = "/precificador/upload/";
const SECOND_FETCH_DELAY_MS = 5_000; // 15s após o upload bem-sucedido
const POLL_INTERVAL_MS = 30_000; // 1min entre verificações de status

const REQUIRED_COLUMNS = [
	"placa",
	"chassi",
	"fipe_code",
	"modelo",
	"ano_modelo",
	"combustivel",
	"preco",
	"data_referencia",
];

const statusLabel: Record<UploadStatus, string> = {
	PENDING: "Pendente",
	PROCESSING: "Processando",
	MATCHING: "Matching",
	DONE: "Concluído",
	FAILED: "Falhou",
};

const statusColors: Record<UploadStatus, { bg: string; fg: string }> = {
	PENDING: { bg: palette.secondary.main, fg: StatusColor["1"] },
	PROCESSING: { bg: palette.background.default, fg: palette.primary.main },
	MATCHING: { bg: palette.warning.main, fg: "#FFFFFF" },
	DONE: { bg: palette.success.main, fg: palette.success.contrastText },
	FAILED: { bg: palette.error.main, fg: "#FFFFFF" },
};

function formatBytes(n: number) {
	if (!n && n !== 0) return "-";
	const k = 1024;
	const sizes = ["B", "KB", "MB", "GB", "TB"];
	if (n === 0) return "0 B";
	const i = Math.floor(Math.log(n) / Math.log(k));
	return `${(n / Math.pow(k, i)).toFixed(2)} ${sizes[i]}`;
}

function formatDuration(sec?: number | null) {
	if (sec == null) return "-";
	const m = Math.floor(sec / 60);
	const s = sec % 60;
	return `${m}m ${s}s`;
}

function execSecondsWhenDone(r: UploadRow): number | null {
	if ((r.progress_percent ?? 0) !== 100) return null;
	const start = new Date(r.created_at).getTime();
	const end = new Date(r.updated_at).getTime();
	const diff = Math.max(0, Math.round((end - start) / 1000));
	return diff;
}

export default function PrecificadorFipe(): JSX.Element {
	const { setTitleBar, setPathTitleBar } = useCore() as any;
	const { user } = useAuth();
	// Guard front-end (o back também deve validar):
	const isAdmin = user?.profile === "3";
	const [searchParams, setSearchParams] = useSearchParams();
	const activeTab: PrecificadorTab =
		searchParams.get("tab") === DOOR_RULES_TAB
			? DOOR_RULES_TAB
			: "emplacamentos";

	// Upload
	const [file, setFile] = useState<File | null>(null);
	const [isDropped, setIsDropped] = useState(false);
	const [uploading, setUploading] = useState(false);

	// Histórico
	const [loading, setLoading] = useState<boolean>(true);
	const [rows, setRows] = useState<UploadRow[]>([]);
	const [count, setCount] = useState<number>(0);
	const [page, setPage] = useState<number>(1);
	const [pageSize, setPageSize] = useState<number>(20);

	// timers
	const pollIntervalRef = useRef<number | null>(null);
	const secondFetchTimeoutRef = useRef<number | null>(null);

	// Filtros
	const [statusFilter, setStatusFilter] = useState<UploadStatus | "">("");

	// Modal Schema
	const [schemaOpen, setSchemaOpen] = useState(false);

	const [deletingId, setDeletingId] = useState<string | null>(null);
	const [matchingIds, setMatchingIds] = useState<Record<string, boolean>>({});

	useLayoutEffect(() => {
		setTitleBar("Precificador Fipe — Gerenciamento");
		setPathTitleBar(
			activeTab === DOOR_RULES_TAB ? "/new-door-rule" : undefined,
		);
	}, [activeTab]);

	const { getRootProps, getInputProps, isDragActive, isDragReject, open } =
		useDropzone({
			accept: {
				"text/csv": [".csv"],
				"application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
					".xlsx",
				],
			},
			maxFiles: 1,
			multiple: false,
			noClick: true, // habilitamos o clique pelo botão "Selecionar planilha"
			onDropAccepted: (acceptedFiles) => {
				setFile(acceptedFiles[0]);
				setIsDropped(true);
				toast.dismiss();
				toast.info("Arquivo pronto para enviar.", { autoClose: 3000 });
			},
			onDropRejected: () => {
				toast.warning("Formato inválido. Envie .xlsx ou .csv.");
			},
		});

	async function handleUpload() {
		if (!file) return;
		try {
			setUploading(true);
			const fd = new FormData();
			fd.append("file", file);

			const resp = await api.post(BASE_PATH, fd, "multipart/form-data");

			if ((resp as any)?.status >= 400) {
				toast.error("Falha ao enviar arquivo.");
			} else if (resp?.data.upload_id) {
				toast.success("Arquivo recebido. Iniciando processamento.");
				setFile(null);
				setIsDropped(false);

				// 1) atualiza imediatamente a lista
				await fetchUploads(1);

				// 2) agenda uma segunda atualização após X segundos
				if (secondFetchTimeoutRef.current)
					clearTimeout(secondFetchTimeoutRef.current);
				secondFetchTimeoutRef.current = window.setTimeout(async () => {
					if (!isMountedRef.current) return;
					const hasProcessing = await fetchUploadsRef.current(1);

					if (hasProcessing) startPolling(POLL_INTERVAL_MS);
				}, SECOND_FETCH_DELAY_MS);
			}
		} catch {
			toast.error("Erro no upload. Tente novamente.");
		} finally {
			setUploading(false);
		}
	}

	async function handleDelete(id: string) {
		if (!id) return;
		const confirm = window.confirm(
			"Tem certeza que deseja excluir este upload e todas os veículos relacionados?",
		);
		if (!confirm) return;

		try {
			setDeletingId(id);
			const resp = await api.destroy(`${BASE_PATH}${id}/`);
			if ((resp as any)?.status >= 400) {
				toast.error(
					resp?.data?.detail ??
						"Não foi possível excluir. Verifique o status do upload.",
				);
				return;
			}

			// Se a página atual ficou sem itens, volte uma página (se possível)
			const remaining = count - 1;
			const lastPage = Math.max(1, Math.ceil(remaining / pageSize));
			const targetPage = Math.min(page, lastPage);

			await fetchUploads(targetPage);
			toast.success("Upload excluído com sucesso.");
		} catch (e: any) {
			// Backend deve retornar 400/409 quando PROCESSING
			toast.error(
				e?.response?.data?.detail ??
					"Não foi possível excluir. Verifique o status do upload.",
			);
		} finally {
			setDeletingId(null);
		}
	}

	async function handleMatch(id: string) {
		if (!id) return;

		try {
			setMatchingIds((prev) => ({ ...prev, [id]: true }));
			const resp = await api.post(`${BASE_PATH}${id}/match/`, {});
			if ((resp as any)?.status >= 400) {
				toast.error("Não foi possível iniciar o matching. Tente novamente.");
				return;
			}
			toast.success("Matching enfileirado.");
			await fetchUploads();
			startPolling(POLL_INTERVAL_MS);
		} catch (e: any) {
			toast.error(
				e?.response?.data?.detail ??
					"Não foi possível iniciar o matching. Verifique o status do upload.",
			);
		} finally {
			setMatchingIds((prev) => {
				const next = { ...prev };
				delete next[id];
				return next;
			});
		}
	}

	const queryString = useMemo(() => {
		const params = new URLSearchParams();
		params.set("page", String(page));
		if (pageSize) params.set("page_size", String(pageSize));
		if (statusFilter) params.set("status", statusFilter);
		return params.toString();
	}, [page, pageSize, statusFilter]);

	// evita condições de corrida em desmontagem
	const isMountedRef = useRef(true);
	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
		};
	}, []);

	// limpar timers
	function clearTimers() {
		if (pollIntervalRef.current) {
			clearInterval(pollIntervalRef.current);
			pollIntervalRef.current = null;
		}
		if (secondFetchTimeoutRef.current) {
			clearTimeout(secondFetchTimeoutRef.current);
			secondFetchTimeoutRef.current = null;
		}
	}

	// inicia/para o polling
	function startPolling(intervalMs = POLL_INTERVAL_MS) {
		if (pollIntervalRef.current) return; // já rodando
		pollIntervalRef.current = window.setInterval(async () => {
			// 1) atualiza lista visível respeitando filtros/paginação atuais
			const hasProcessing = await fetchUploadsRef.current();

			if (!hasProcessing) {
				clearTimers(); // encerra polling automaticamente quando tudo finalizar
			}
		}, intervalMs);
	}

	async function fetchUploads(goToPage?: number) {
		try {
			const effectivePage = goToPage ?? page;
			const limit = pageSize;
			const offset = (effectivePage - 1) * pageSize;

			const params = new URLSearchParams({
				limit: String(limit),
				offset: String(offset),
			});
			if (statusFilter) params.set("status", statusFilter);
			const url = `${BASE_PATH}?${params.toString()}`;
			const res = (await api.get(url)) as ListResponse;

			const payload = (res as any)?.content ?? res;
			setRows(payload.results || []);
			setCount(payload.count || 0);

			if (goToPage) setPage(goToPage);

			const hasProcessing = (payload.results || []).some(
				(x: UploadRow) => x.status === "PROCESSING" || x.status === "MATCHING",
			);

			return hasProcessing;
		} catch {
			toast.error("Erro ao carregar histórico.");
		} finally {
			setLoading(false);
		}
	}

	const fetchUploadsRef = useRef(fetchUploads);
	fetchUploadsRef.current = fetchUploads;

	useEffect(() => {
		if (isAdmin) fetchUploads();
	}, [queryString, isAdmin]);

	useEffect(() => {
		let cancelled = false;

		(async () => {
			if (!isAdmin) return;
			const hasProcessing = await fetchUploads();

			if (!cancelled && hasProcessing) {
				startPolling(POLL_INTERVAL_MS);
			}
		})();

		return () => {
			cancelled = true;
			clearTimers(); // parar tudo ao sair da tela
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAdmin]);

	if (!isAdmin)
		return (
			<>
				<ToastContainer />
				<NotAuthorized />
			</>
		);

	return (
		<>
			<ToastContainer />

			<Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
				<Tabs
					value={activeTab}
					onChange={(_event, value: PrecificadorTab) =>
						setSearchParams(
							value === DOOR_RULES_TAB ? { tab: DOOR_RULES_TAB } : {},
						)
					}
				>
					<Tab label="Emplacamentos" value="emplacamentos" />
					<Tab label="Regras de portas" value={DOOR_RULES_TAB} />
				</Tabs>
			</Box>

			{activeTab === DOOR_RULES_TAB ? (
				<DoorRulesTab />
			) : (
				<>
					{/* UPLOAD */}
					<Paper sx={{ p: 2, mb: 3, borderRadius: "12px" }}>
						<Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
							Upload da planilha de emplacamento
						</Typography>

						<Grid container spacing={2} alignItems="center">
							<Grid item xs={12}>
								<Box
									{...getRootProps()}
									sx={{
										p: 3,
										borderRadius: "12px",
										border: "1px dashed #D6D6D6",
										display: "flex",
										alignItems: "center",
										gap: 1.5,
										bgcolor: isDragActive ? "#F9FAFB" : "transparent",
										justifyContent: "space-between",
										flexWrap: "wrap",
									}}
								>
									<input {...getInputProps()} />
									<Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
										{isDragActive ? (
											isDragReject ? (
												<>
													<BiBlock /> <b>Apenas .xlsx ou .csv</b>
												</>
											) : (
												<>
													<SiMicrosoftexcel /> <b>Solte para enviar</b>
												</>
											)
										) : isDropped && file ? (
											<Box
												sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
											>
												<SiMicrosoftexcel />
												<span>
													Planilha <b>{file.name}</b> pronta para upload (
													{formatBytes(file.size)})
												</span>
												<IconButton
													size="small"
													title="Limpar seleção"
													onClick={(e) => {
														e.stopPropagation(); // evita qualquer ação do dropzone
														setFile(null);
														setIsDropped(false);
														toast.info("Seleção de planilha removida.", {
															autoClose: 3000,
														});
													}}
												>
													<CloseRoundedIcon fontSize="small" />
												</IconButton>
											</Box>
										) : (
											<>
												<SiMicrosoftexcel /> Arraste sua planilha aqui ou use o
												botão ao lado
											</>
										)}
									</Box>

									<Box sx={{ display: "flex", gap: 1 }}>
										{/* <Button variant="outlined" onClick={() => setSchemaOpen(true)}>
		                  Ver layout obrigatório
		                </Button> */}
										<Button color="success" variant="contained" onClick={open}>
											Selecionar planilha
										</Button>
									</Box>
								</Box>
							</Grid>

							<Grid item>
								<Button
									variant="contained"
									color="success"
									disabled={!file || uploading}
									onClick={handleUpload}
								>
									{uploading ? "Enviando..." : "Enviar planilha"}
								</Button>
							</Grid>
						</Grid>
					</Paper>

					<Paper sx={{ p: 2, mb: 2, borderRadius: "12px" }}>
						<Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>
							Histórico das planilhas de emplacamento
						</Typography>
						{/* FILTROS DO HISTÓRICO */}
						<Grid container gap={2} alignItems="center" sx={{ mt: 2, mb: 2 }}>
							<FormControl sx={{ minWidth: 220 }}>
								<Select
									value={statusFilter}
									onChange={(e: SelectChangeEvent) => {
										setStatusFilter(e.target.value as UploadStatus | "");
										setPage(1);
									}}
									displayEmpty
								>
									<MenuItem value="">Status (todos)</MenuItem>
									{(
										[
											"PENDING",
											"PROCESSING",
											"DONE",
											"FAILED",
										] as UploadStatus[]
									).map((s) => (
										<MenuItem key={s} value={s}>
											{statusLabel[s]}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<FormControl sx={{ minWidth: 160 }}>
								<Select
									value={pageSize}
									onChange={(e) => {
										setPageSize(Number(e.target.value));
										setPage(1);
									}}
								>
									<MenuItem value={20}>20 por página</MenuItem>
									<MenuItem value={50}>50 por página</MenuItem>
									<MenuItem value={100}>100 por página</MenuItem>
								</Select>
							</FormControl>
						</Grid>

						{/* HISTÓRICO */}
						<Paper
							sx={{
								width: "100%",
								overflow: "hidden",
								borderRadius: "15px",
								boxShadow: "0px 0px 3px 1px rgba(0,0,0,0.2)",
							}}
						>
							<TableContainer>
								<Table sx={{ minWidth: 650 }} aria-label="uploads table">
									<TableHead sx={{ background: "#F9FAFB" }}>
										<TableRow>
											{[
												"Status",
												"Progresso",
												"Arquivo",
												"Linhas Processadas",
												"Erros",
												"Total de Linhas",
												"Tamanho",
												"Tempo de Processamento",
												"Criado em",
												"Atualizado em",
												"Ações",
											].map((col) => (
												<TableCell
													key={col}
													align="center"
													sx={{
														color: "#6B7280",
														background: "#F9FAFB",
														fontWeight: 600,
													}}
												>
													{col}
												</TableCell>
											))}
										</TableRow>
									</TableHead>

									<TableBody>
										{loading ? (
											<TableRow>
												<TableCell colSpan={11} align="center">
													Carregando…
												</TableCell>
											</TableRow>
										) : rows.length === 0 ? (
											<TableRow>
												<TableCell colSpan={11} align="center">
													<Alert severity="info" variant="filled">
														Nenhuma planilha encontrada
													</Alert>
												</TableCell>
											</TableRow>
										) : (
											rows.map((r) => {
												const colors = statusColors[r.status];
												return (
													<TableRow key={r.id}>
														<TableCell align="center">
															<Chip
																label={statusLabel[r.status]}
																sx={{
																	backgroundColor: colors.bg,
																	color: colors.fg,
																	fontWeight: "bold",
																}}
															/>
														</TableCell>

														<TableCell align="center">
															{r.progress_percent ?? 0}%
														</TableCell>

														<TableCell align="center">
															<Tooltip
																title={r.original_name}
																placement="right"
															>
																<span>
																	{r.original_name.length > 24
																		? r.original_name.slice(0, 24) + "…"
																		: r.original_name}
																</span>
															</Tooltip>
														</TableCell>

														{/* Linhas Processadas */}
														<TableCell align="center">
															{r.row_count ?? 0}
														</TableCell>

														<TableCell align="center">
															{r.error_count ?? 0}
														</TableCell>

														{/* Total de Linhas */}
														<TableCell align="center">
															{r.total_rows ?? "-"}
														</TableCell>

														<TableCell align="center">
															{formatBytes(r.size_bytes)}
														</TableCell>
														{/* Tempo de Execução (só quando 100%) */}
														<TableCell align="center">
															{(() => {
																const sec = execSecondsWhenDone(r);
																return sec == null ? "-" : formatDuration(sec);
															})()}
														</TableCell>
														<TableCell align="center">
															{new Date(r.created_at).toLocaleString()}
														</TableCell>
														<TableCell align="center">
															{new Date(r.updated_at).toLocaleString()}
														</TableCell>

														<TableCell align="center">
															<Box
																sx={{
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "center",
																	gap: 0.5,
																}}
															>
																<Tooltip
																	title={
																		r.status === "PROCESSING" ||
																		r.status === "MATCHING"
																			? "Não é possível iniciar o matching enquanto processa ou está sendo comparado"
																			: "Reprocessar matching desta planilha"
																	}
																>
																	<span>
																		<IconButton
																			size="small"
																			color="primary"
																			disabled={
																				r.status === "PROCESSING" ||
																				r.status === "MATCHING" ||
																				!!matchingIds[r.id]
																			}
																			onClick={() => handleMatch(r.id)}
																		>
																			<RefreshRoundedIcon />
																		</IconButton>
																	</span>
																</Tooltip>

																<Tooltip
																	title={
																		r.status === "PROCESSING" ||
																		r.status === "MATCHING"
																			? "Não é possível excluir enquanto processa ou está sendo comparado"
																			: "Excluir upload"
																	}
																>
																	<span>
																		<IconButton
																			size="small"
																			color="error"
																			disabled={
																				r.status === "PROCESSING" ||
																				r.status === "MATCHING" ||
																				deletingId === r.id
																			}
																			onClick={() => handleDelete(r.id)}
																		>
																			<DeleteOutlineRoundedIcon />
																		</IconButton>
																	</span>
																</Tooltip>
															</Box>
														</TableCell>

														{/* <TableCell align="center">
		                        <IconButton
		                          size="small"
		                          onClick={() => window.open(`/internal-products/precificador-fipe/emplacamentos/uploads/${r.id}/errors`, "_blank")}
		                          title="Ver erros"
		                        >
		                          <MoreVertIcon/>
		                        </IconButton>
		                      </TableCell> */}
													</TableRow>
												);
											})
										)}
									</TableBody>
								</Table>
							</TableContainer>
						</Paper>

						{/* paginação */}
						<Box sx={{ mt: 2 }}>
							<Paginator
								count={count}
								pageNumber={page}
								qtdResults={pageSize}
								onChange={(_e, val) => setPage(val)}
							/>
						</Box>
					</Paper>
				</>
			)}

			{/* Modal Schema */}
			<Dialog
				open={schemaOpen}
				onClose={() => setSchemaOpen(false)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Layout obrigatório da planilha</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 1.5 }}>
						A planilha deve conter as colunas abaixo (ordem indiferente). O
						endpoint rejeita uploads que não seguirem este layout.
					</Typography>
					<ul style={{ marginLeft: 16 }}>
						{REQUIRED_COLUMNS.map((c) => (
							<li key={c}>
								<code>{c}</code>
							</li>
						))}
					</ul>
				</DialogContent>
			</Dialog>
		</>
	);
}
